import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, DragEndEvent, useDraggable, useDroppable, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftRight } from 'lucide-react';
import { Skill } from '../../../models/job';
import { Button } from '@/components/ui/atoms/Button';
import { Tag } from '@/components/ui/atoms/Tag';

interface DraggableSkillClassifierProps {
  skills: Skill[];
  onSkillsChange: (skills: Skill[]) => void;
  type: 'language' | 'framework';
}

type Classified = 'must_have' | 'nice_to_have';
const CLASSIFIED_BUCKETS: Classified[] = ['must_have', 'nice_to_have'];

interface ClassifiedSkillChipProps {
  id: string;
  content: string;
  type: 'language' | 'framework';
  onFlip: (id: string) => void;
  onDelete: (id: string) => void;
  removeAriaLabel: string;
}

/** Classified skill chip — drag/flip on wrapper; Tag owns wrap + close. */
const ClassifiedSkillChip: React.FC<ClassifiedSkillChipProps> = ({
  id,
  content,
  type,
  onFlip,
  onDelete,
  removeAriaLabel,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });

  const dragStyle = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  return (
    <motion.span
      ref={setNodeRef}
      layoutId={`skill-${id}`}
      layout="position"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: isDragging ? 0.5 : 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.85 }}
      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
      style={dragStyle}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        e.stopPropagation();
        onFlip(id);
      }}
      className="inline-flex min-w-0 max-w-full cursor-pointer select-none"
    >
      <Tag
        intent={type === 'language' ? 'info' : 'primary'}
        variant="subtle"
        icon={<ArrowLeftRight className="text-dark-invirtus/40" aria-hidden />}
        closable
        onClose={(e) => {
          e.stopPropagation();
          onDelete(id);
        }}
        closeAriaLabel={removeAriaLabel}
        title={content}
        wrap
      >
        {content}
      </Tag>
    </motion.span>
  );
};

interface DroppableColumnProps {
  id: Classified;
  title: string;
  skills: Skill[];
  onAddSkill: (skillName: string) => void;
  onFlip: (id: string) => void;
  onDelete: (id: string) => void;
  onRemoveAriaLabel: (skillName: string) => string;
  type: 'language' | 'framework';
  addLabel: string;
  newSkillPlaceholder: string;
}

const DroppableColumn: React.FC<DroppableColumnProps> = ({
  id,
  title,
  skills,
  onAddSkill,
  onFlip,
  onDelete,
  onRemoveAriaLabel,
  type,
  addLabel,
  newSkillPlaceholder,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  const [isEditing, setIsEditing] = useState(false);
  const [newSkill, setNewSkill] = useState('');

  const submitNew = () => {
    if (newSkill.trim()) onAddSkill(newSkill.trim());
    setNewSkill('');
    setIsEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      className={`relative flex min-w-0 flex-col overflow-hidden rounded-lg border-2 border-dashed transition-colors h-full min-h-[160px] ${
        isOver ? 'border-purple bg-purple/5' : 'border-gray-300'
      }`}
    >
      <div
        className="flex min-h-0 flex-1 flex-wrap items-start content-start gap-1.5 overflow-x-hidden overflow-y-auto p-3 [scrollbar-width:thin] [scrollbar-color:theme(colors.gray.300)_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent"
      >
        <AnimatePresence initial={false} mode="popLayout">
          {skills.map((skill) => (
            <ClassifiedSkillChip
              key={skill.name}
              id={skill.name}
              content={skill.name}
              type={type}
              onFlip={onFlip}
              onDelete={onDelete}
              removeAriaLabel={onRemoveAriaLabel(skill.name)}
            />
          ))}
        </AnimatePresence>

        {isEditing ? (
          <Tag
            intent={type === 'language' ? 'info' : 'primary'}
            variant="subtle"
          >
            <input
              type="text"
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              onBlur={submitNew}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitNew();
                }
                if (e.key === 'Escape') {
                  setNewSkill('');
                  setIsEditing(false);
                }
              }}
              autoFocus
              placeholder={newSkillPlaceholder}
            />
          </Tag>
        ) : (
          <Button
            type="outlined"
            intent="primary"
            size="xs"
            dashed
            onClick={() => setIsEditing(true)}
            label={addLabel}
            icon={<span className="leading-none">+</span>}
          />
        )}
      </div>

      <div className="shrink-0 bg-white text-center py-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {title}
        </span>
      </div>
    </div>
  );
};

/** Two-column must-have / nice-to-have classifier for job-flow modals (Create/Refine + Edit). */
export const DraggableSkillClassifier: React.FC<DraggableSkillClassifierProps> = ({
  skills,
  onSkillsChange,
  type,
}) => {
  const { t } = useTranslation('dashboard');
  const [items, setItems] = useState<Record<Classified, Skill[]>>({
    must_have: [],
    nice_to_have: [],
  });

  // Drop the "Unclassified" bucket: any incoming `uncertain` skill is promoted
  // to "Nice to Have" so the user always sees just two clear columns. We bubble
  // the normalized list up once on first sight of uncertain skills.
  const normalizedRef = useRef(false);

  useEffect(() => {
    const must = skills.filter((s) => s.importance === 'must_have');
    const nice = skills
      .filter((s) => s.importance !== 'must_have')
      .map((s) =>
        s.importance === 'uncertain' ? { ...s, importance: 'nice_to_have' as const } : s
      );

    setItems({ must_have: must, nice_to_have: nice });

    if (!normalizedRef.current && skills.some((s) => s.importance === 'uncertain')) {
      normalizedRef.current = true;
      onSkillsChange([...must, ...nice]);
    }
  }, [skills, onSkillsChange]);

  // Touch-friendly sensors: pointer needs a small move to start a drag so taps
  // (used for flip) aren't accidentally treated as drags.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  const commit = (next: Record<Classified, Skill[]>) => {
    setItems(next);
    onSkillsChange([...next.must_have, ...next.nice_to_have]);
  };

  const moveSkill = (skillName: string, to: Classified) => {
    const from: Classified = items.must_have.some((s) => s.name === skillName)
      ? 'must_have'
      : 'nice_to_have';
    if (from === to) return;
    const skill = items[from].find((s) => s.name === skillName);
    if (!skill) return;
    commit({
      ...items,
      [from]: items[from].filter((s) => s.name !== skillName),
      [to]: [...items[to], { ...skill, importance: to }],
    });
  };

  const flipSkill = (skillName: string) => {
    const inMust = items.must_have.some((s) => s.name === skillName);
    moveSkill(skillName, inMust ? 'nice_to_have' : 'must_have');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const overId = event.over?.id?.toString();
    if (!overId || !CLASSIFIED_BUCKETS.includes(overId as Classified)) return;
    moveSkill(event.active.id.toString(), overId as Classified);
  };

  const addSkill = (importance: Classified) => (skillName: string) => {
    const normalized = skillName.trim();
    if (!normalized) return;
    const exists = [...items.must_have, ...items.nice_to_have].some(
      (s) => s.name.toLowerCase() === normalized.toLowerCase()
    );
    if (exists) return;
    commit({
      ...items,
      [importance]: [...items[importance], { name: normalized, importance }],
    });
  };

  const deleteSkill = (skillName: string) => {
    commit({
      must_have: items.must_have.filter((s) => s.name !== skillName),
      nice_to_have: items.nice_to_have.filter((s) => s.name !== skillName),
    });
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 h-full min-h-0">
        <DroppableColumn
          id="must_have"
          title={t('create_job_modal.skills_classifier.must_have')}
          skills={items.must_have}
          onAddSkill={addSkill('must_have')}
          onFlip={flipSkill}
          onDelete={deleteSkill}
          onRemoveAriaLabel={(skill) => t('create_job_modal.skills_classifier.remove_skill', { skill })}
          type={type}
          addLabel={t('create_job_modal.skills_classifier.add')}
          newSkillPlaceholder={t('create_job_modal.skills_classifier.new_skill')}
        />
        <DroppableColumn
          id="nice_to_have"
          title={t('create_job_modal.skills_classifier.nice_to_have')}
          skills={items.nice_to_have}
          onAddSkill={addSkill('nice_to_have')}
          onFlip={flipSkill}
          onDelete={deleteSkill}
          onRemoveAriaLabel={(skill) => t('create_job_modal.skills_classifier.remove_skill', { skill })}
          type={type}
          addLabel={t('create_job_modal.skills_classifier.add')}
          newSkillPlaceholder={t('create_job_modal.skills_classifier.new_skill')}
        />
      </div>
    </DndContext>
  );
};
